import React, { useEffect, useRef } from "react"
import * as echarts from "echarts"
import styled from "styled-components"

interface WrapperProps {
  height: string
  width: string
}

export const StackedBarChartWrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  height: ${(props) => props.height};
  width: ${(props) => props.width};
  position: relative;
`

interface ChartData {
  name: string // 지역 이름
  date: string // 날짜
  value: number // 값 (사용량, 발전량, 배출량, 감축량 등)
}

interface ChartProps {
  data: ChartData[] // 차트 데이터 배열
  height: string // 차트 높이
  width: string // 차트 너비
  color?: string[] // 색상 배열 (선택적)
}

const StackedBarChartForMonitoring: React.FC<ChartProps> = ({
  data,
  color,
  height,
  width,
}) => {
  const chartRef = useRef<HTMLDivElement>(null)

  // X축 데이터 생성
  const generateXAxisData = (): string[] => {
    const validData = Array.isArray(data) ? data : []
    return Array.from(new Set(validData.map((item) => item.date)))
  }

  // Series 데이터 생성
  const generateSeriesData = (): echarts.SeriesOption[] => {
    // data가 배열인지 확인
    if (!Array.isArray(data)) {
      console.error("data is not Array.")
      return [] // 빈 배열 반환
    }

    const seriesMap: Record<string, number[]> = {}

    data.forEach((item) => {
      if (!seriesMap[item.name]) {
        seriesMap[item.name] = []
      }

      const index = generateXAxisData().indexOf(item.date)

      if (index >= 0) {
        seriesMap[item.name][index] = Number(item.value)
      }
    })

    return Object.entries(seriesMap).map(([name, values]) => ({
      name,
      type: "bar",
      stack: "stack",
      emphasis: { focus: "series" },
      data: values.map((v) => {
        if (v !== undefined) {
          // 특정 조건에 따라 소수점 자릿수 조정
          return Number(v.toFixed(2)) // 그렇지 않으면 소수점 2자리
        }
        return 0
      }),
    }))
  }

  useEffect(() => {
    if (chartRef.current) {
      const myChart = echarts.init(chartRef.current)

      const xAxisData = generateXAxisData()
      const seriesData = generateSeriesData()

      const option: echarts.EChartsOption = {
        color: color || ["#1353AD", "#327EFB", "#41D0DF"],
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
        },
        grid: {
          top: "10%",
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: xAxisData,
        },
        yAxis: {
          type: "value",
        },
        series: seriesData,
      }

      myChart.setOption(option)

      // 이벤트 리스너에서 myChart.resize를 호출
      const handleResize = () => {
        myChart.resize()
      }

      window.addEventListener("resize", handleResize)

      return () => {
        window.removeEventListener("resize", handleResize)
        myChart.dispose()
      }
    }
  }, [data, color])

  return (
    <StackedBarChartWrapper width={width} height={height}>
      <div
        ref={chartRef}
        style={{ width: "100%", height: "100%", margin: 0, padding: 0 }}
      />
    </StackedBarChartWrapper>
  )
}

export default StackedBarChartForMonitoring
